import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import {
  IMemberTypewiseSectionConfigGridFilter,
  IMemberTypewiseSectionConfigGridModel,
} from "../api/memberTypewiseSectionConfig.types";
import { Grid, Chip } from "@mui/material";
import { Resolver, useForm } from "react-hook-form";
import { AutoCompleteElement } from "components/AutoComplete";
import ActionColumn from "components/ActionColumn";
import request from "../api/memberTypewiseSectionConfig.requests";
import { LovelyGridProvider } from "contexts/LovelyGridContext";
import { FilterContainer } from "components/Containers/FilterContainer";
import { GridContainer } from "components/Containers/GridContainer";
import LovelyGrid from "components/LovelyGrid";
import { IDropdownModel } from "common/models/IDropdownModel";
import View from "./View";
import dayjs from "dayjs";
import useAuth from "hooks/useAuth";
import { GetMemberTypeValues } from "features/memberType/api/hooks";

export default function List() {
  const resolver: Resolver<IMemberTypewiseSectionConfigGridFilter> = async (
    values
  ) => {
    return {
      values: values.memberTypeID ? values : {},
      errors: {},
    };
  };
  const { modules } = useAuth();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IMemberTypewiseSectionConfigGridFilter>({ resolver });

  const columns: GridColDef<IMemberTypewiseSectionConfigGridModel>[] = useMemo(
    () => [
      {
        field: "memberTypeName",
        headerName: "Member Type",
        filterable: false,
        flex: 1,
      },
      {
        field: "sectionName",
        headerName: "Section",
        filterable: false,
        flex: 1,
      },
      {
        field: "isOvernightIssue",
        filterable: false,
        headerName: "Is Overnight Issue?",
        flex: 0,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <>
            {params.value ? (
              <Chip
                sx={{ borderRadius: 10 }}
                label="Yes"
                size="small"
                color="success"
                variant="filled"
              />
            ) : (
              <Chip
                sx={{ borderRadius: 10 }}
                label="No"
                size="small"
                color="error"
                variant="filled"
              />
            )}
          </>
        ),
      },
      {
        field: "overnightIssueMaxItems",
        headerName: "Overnight Issue Max Items",
        flex: 1,
      },
      {
        field: "isOnPremisesIssue",
        headerName: "Is On Premises Issue?",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <>
            {params.value ? (
              <Chip
                sx={{ borderRadius: 10 }}
                label="Yes"
                size="small"
                color="success"
                variant="filled"
              />
            ) : (
              <Chip
                sx={{ borderRadius: 10 }}
                label="No"
                size="small"
                color="error"
                variant="filled"
              />
            )}
          </>
        ),
      },
      {
        field: "onPremisesIssueEndTime",
        headerName: "On Premises Issue End Time",
        flex: 1,
        renderCell: (params) => {
          return (
            <>
              {params.value !== null
                ? dayjs(params.value).format("hh:mm A")
                : "-"}
            </>
          );
        },
      },
      { field: "libraryName", headerName: "Library Name", flex: 1 },
      {
        field: "",
        type: "custom",
        flex: 1,
        sortable: false,
        headerName: "Actions",
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <>
            <ActionColumn
              permissions={modules?.memberTypewiseSectionConfig.permissions}
              id={params.row.memberTypewiseSectionConfigID}
              dataKey="memberTypewiseSectionConfig"
              deleteMutation={{ mutationFn: request.delete }}
              modalFetcher={request.getViewById}
            />
          </>
        ),
      },
    ],
    []
  );

  const initFilter: IMemberTypewiseSectionConfigGridFilter = {
    memberTypeID: null,
  };

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    []
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  return (
    <>
      <LovelyGridProvider initFilter={initFilter}>
        <FilterContainer
          clearForm={reset}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
        >
          <Grid container>
            <Grid item xs={6} md={4} lg={3}>
              <AutoCompleteElement
                options={memberTypeOptions}
                control={control}
                name="memberTypeID"
                label="Member Type"
              />
            </Grid>
          </Grid>
        </FilterContainer>

        <GridContainer
          add={modules?.memberTypewiseSectionConfig.permissions.Add}
        >
          <LovelyGrid
            queryKey="memberTypewiseSectionConfig"
            columns={columns}
            fetcher={request.getAll}
            id={(item) => item.memberTypewiseSectionConfigID}
            modalElement={View}
            modalFetcher={request.getViewById}
          ></LovelyGrid>
        </GridContainer>
      </LovelyGridProvider>
    </>
  );
}
